import React from "react";
import { Link } from "react-router-dom";
import Footer from "../../components/Footer";

export default function OLEDUseCase() {
  return (
    <>
    <section
      id="oleds"
      className="min-h-screen pt-[96px] pb-20 px-6 sm:px-8 bg-[#1A1631] text-white scroll-mt-[96px]"
    >
      <div className="max-w-5xl mx-auto space-y-10">
        {/* Back Link */}
        <Link
          to="/"
          className="text-[#EF7C34] hover:underline text-sm flex items-center mb-6"
        >
          ← Back to main site
        </Link>

        {/* Heading + Intro */}
        <div className="space-y-4">
          <h1 className="sm:text-2xl font-bold bg-gradient-to-r from-[#EF7C34] to-[#F89B3F] bg-clip-text text-transparent">
            OLEDs (Organic Light-Emitting Diodes)
          </h1>
	<p className="text-slate-300 space-y-2">
	  OLED development has advanced through several generations:
	  <strong> 1st Gen</strong> OLEDs used fluorescent emitters with internal quantum efficiencies around 25%;
	  <strong> 2nd Gen</strong> OLEDs introduced phosphorescent materials that harvest both singlet and triplet excitons, achieving near 100% efficiency but relying on scarce heavy metals;
	  and <strong>3rd Gen</strong> OLEDs leverage thermally activated delayed fluorescence (TADF), enabling fully organic materials to achieve high efficiency without metals.
	  However, achieving fast reverse intersystem crossing (RISC) — critical to TADF performance — depends sensitively on high-frequency vibrational modes, as shown in our recent theoretical work. <sup>[1]</sup>
	  <br /><br />
	  One advanced strategy, known as <strong>hyperfluorescence</strong>, combines a TADF sensitiser with a narrowband fluorescent emitter to boost colour purity and efficiency.
	  Another promising direction is the development of <strong>INVEST</strong> (inverted singlet–triplet gap) emitters, which flip the conventional energy hierarchy to bypass the need for RISC altogether.
	  We used high-throughput screening to identify new INVEST cores with potential for next-generation emitters.<sup>[2]</sup>
	  <br /><br />
	  Beyond RISC or ΔE<sub>ST</sub>, our recent screening studies highlight that low exciton–vibration coupling can enhance exciton delocalisation and improve spectral sharpness<sup>[3]</sup> — a key requirement for ultra-high colour purity in display applications.
	</p>
        </div>

        {/* How Apoello helps */}
        <div className="space-y-4">
          <h2 className="text-2xl font-semibold text-orange-300">How DiaDEM™ helps</h2>
          <ul className="list-disc list-inside text-slate-300 space-y-2">
            <li>
              Search a calibrated database of over 50 million molecules for key properties like air stability, absorption energies, singlet–triplet gaps, oscillator strengths, HOMO–LUMO gaps.
            </li>
            <li>
              Identify promising emitters (e.g. TADF, INVEST) for enhanced efficiency and colour tuning.
            </li>
            <li>
              Run calculations on emission wavelengths, charge mobility and solid-state packing — all in the cloud.
            </li>
            <li>
              Order candidate emitters or precursors directly to your lab.
            </li>
          </ul>
        </div>

        {/* Why it matters */}
        <div className="space-y-4">
          <h2 className="text-2xl font-semibold text-orange-300">Why it matters</h2>
          <p className="text-slate-300 max-w-3xl">
            OLED development is bottlenecked by slow trial-and-error synthesis and testing.
            DiaDEM™ enables rapid in silico screening to pinpoint viable emitters before you step into the lab — accelerating R&D, reducing cost, and improving final device performance.
          </p>
        </div>

        {/* References */}
        <div className="space-y-4 pt-10">
          <h2 className="text-2xl font-semibold text-orange-300">
  References from our group
</h2>

          <ol className="list-decimal list-inside text-slate-400 space-y-1 text-sm">
            <li>
              Pizza, T. et al. <i>The Importance of High-Frequency Modes in the Prediction of RISC Rates for TADF Molecules</i>, J. Phys. Chem. Lett. (2025).{" "}
              <a href="https://doi.org/10.1021/acs.jpclett.5c00176" target="_blank" rel="noopener noreferrer" className="text-[#F89B3F] underline">Link ↗</a>
            </li>
            <li>
              Omar, Ö. H. et al. <i>Identification of Unknown Inverted Singlet-Triplet Cores by High-Throughput Virtual Screening</i>, J. Am. Chem. Soc. (2023).{" "}
              <a href="https://doi.org/10.1021/jacs.3c05452" target="_blank" rel="noopener noreferrer" className="text-[#F89B3F] underline">Link ↗</a>
            </li>
            <li>
              Xie, X. & Troisi, A. <i>Identification via Virtual Screening of Emissive Molecules with a Small Exciton-Vibration Coupling</i>, J. Phys. Chem. Lett. (2023).{" "}
              <a href="https://doi.org/10.1021/acs.jpclett.3c00749" target="_blank" rel="noopener noreferrer" className="text-[#F89B3F] underline">Link ↗</a>
            </li>
          </ol>
        </div>

        {/* CTA Button */}
        <div className="pt-8">
          <Link
            to="/diadem-overview"
            className="inline-block px-6 py-3 bg-[#EF7C34] text-white rounded-xl font-medium hover:bg-[#F89B3F] transition"
          >
            Explore the DiaDEM™ Platform →
          </Link>
        </div>
      </div>
    </section>
          {/* ✅ Minimal Footer always sits outside main content */}
      <Footer />
    </>
  );
}
